import { Formik } from 'formik';

import { useQueryClient } from '@tanstack/react-query';
import { createInsurance, CreateInsuranceInput, Insurance, UserProfile } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { useL10n } from '@uplift/hooks';
import { useUserInsurancesQuery, useUserWithProfileQuery } from '@uplift/queries';
import { AddressType, YesNo } from '@uplift/types';
import { AddressVerificationProvider, Button, useWithAddressVerify } from '@uplift/ui-v2';

import {
  StepContainer, StepContent, StepDescription, StepForm, StepHeading, StepNavigator, StepNextButton
} from '../../../components';
import {
  backendBirthDateFormatToFormFormat, formBirthDateFormatToBackendFormat, SELF_PAY_ID
} from '../../../utils';
import { InsuranceInfoNextData } from '../../types';
import { InsuranceInfoForm } from './form';
import { InsuranceInfoFormValues, insuranceInfoValidation } from './validation';

export type InsuranceInfoProps = {
  onBack: () => void;
  onNext: (data: InsuranceInfoNextData) => void;
  isInviteOrDirectFlow: boolean;
  providerId: string;
  insuranceId: string;
  banner?: React.ReactNode;
  isBookingSponsored?: boolean;
};

type InsuranceInfoContentProps = InsuranceInfoProps & {
  initialValues: InsuranceInfoFormValues;
};

const profileToAddressValues = (profile: UserProfile): AddressType => ({
  address1: profile.address.line1 ?? '',
  address2: profile.address.line2 ?? '',
  city: profile.address.city ?? '',
  state: profile.address.stateId ?? '',
  zip: profile.address.zip ?? '',
});

const InsuranceInfoContent: React.VFC<InsuranceInfoContentProps> = (props) => {
  const queryClient = useQueryClient();
  const withAddressVerify = useWithAddressVerify();
  const currentUser = useCurrentuser();
  const { data: userWithProfile } = useUserWithProfileQuery(currentUser.id);
  const l10n = useL10n();

  const onSubmit = withAddressVerify(async (values: InsuranceInfoFormValues) => {
    const isPrimary = values.isPrimarySubscriber === YesNo.Yes;

    const address = isPrimary
      ? profileToAddressValues(userWithProfile.profile)
      : values.address;

    const primarySubscriber = isPrimary
      ? {
        firstName: userWithProfile.user.firstName,
        lastName: userWithProfile.user.lastName,
        dob: userWithProfile.profile.dob,
      }
      : {
        firstName: values.primarySubscriberFirstName,
        lastName: values.primarySubscriberLastName,
        dob: formBirthDateFormatToBackendFormat(values.primarySubscriberDateOfBirth)
      };

    const data: CreateInsuranceInput = {
      isPrimary: isPrimary,
      companyId: values.insuranceProvider,
      groupNumber: values.groupNumber,
      subscriberId: values.memberId,
      address: {
        line1: address.address1,
        line2: address.address2,
        city: address.city,
        stateId: address.state,
        zip: address.zip,
      },
      cardFrontAssetId: values.insuranceCardFront,
      cardBackAssetId: values.insuranceCardBack,
      isSecondaryInsurance: false,
      ...primarySubscriber
    };

    await createInsurance(data);
    await queryClient.invalidateQueries({ queryKey: [ 'user', currentUser.id, 'insurances' ] });

    props.onNext({ insuranceId: data.companyId });
  }, {
    shouldCheck: (_, values) => values.isPrimarySubscriber === YesNo.No,
    initialAddress: props.initialValues.address,
    getAddressFromFormikValues: (values) => values.address,
  });

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={props.initialValues}
      validationSchema={() => insuranceInfoValidation(l10n)}
    >
      {formik => (
        <StepContainer>
          {props.banner}
          <StepContent>
            <StepHeading>Insurance</StepHeading>
            {!props?.isBookingSponsored && <StepDescription>Upload insurance information to ensure you are billed for the correct amount.</StepDescription>}
            <StepForm onSubmit={formik.handleSubmit}>
              <InsuranceInfoForm
                isInviteOrDirectFlow={props.isInviteOrDirectFlow}
                providerId={props.providerId}
              />
            </StepForm>
          </StepContent>
          <StepNavigator onBack={props.onBack}>
            <StepNextButton disabled={!formik.isValid || formik.isSubmitting} />
            {props.isInviteOrDirectFlow && <Button onClick={() => props.onNext({ insuranceId: SELF_PAY_ID })}>Skip Insurance</Button>}
          </StepNavigator>
        </StepContainer>
      )}
    </Formik>
  );
};

const getInitialValues = (selectedInsuranceId: string, insurance?: Insurance): InsuranceInfoFormValues => {
  let isPrimary = true;

  if (insurance?.isPrimary !== undefined && insurance.isPrimary !== null) {
    isPrimary = insurance.isPrimary;
  }

  const data: InsuranceInfoFormValues = {
    isPrimarySubscriber: isPrimary ? YesNo.Yes : YesNo.No,
    insuranceProvider: selectedInsuranceId ?? insurance?.companyId,
    memberId: insurance?.subscriberId ?? '',
    groupNumber: insurance?.groupNumber ?? '',
    insuranceCardFront: insurance?.cardFrontAssetId ?? null,
    insuranceCardBack: insurance?.cardBackAssetId ?? null,
  };

  return {
    ...data,
    primarySubscriberFirstName: getPrimarySubscriberValue(isPrimary, insurance?.firstName ?? ''),
    primarySubscriberLastName: getPrimarySubscriberValue(isPrimary, insurance?.lastName ?? ''),
    primarySubscriberDateOfBirth: getPrimarySubscriberValue(isPrimary, insurance?.dob ? backendBirthDateFormatToFormFormat(insurance.dob) : ''),
    address: {
      address1: getPrimarySubscriberValue(isPrimary, insurance?.address?.line1 ?? ''),
      address2: getPrimarySubscriberValue(isPrimary, insurance?.address?.line2 ?? ''),
      city: getPrimarySubscriberValue(isPrimary, insurance?.address?.city ?? ''),
      state: getPrimarySubscriberValue(isPrimary, insurance?.address?.stateId ?? ''),
      zip: getPrimarySubscriberValue(isPrimary, insurance?.address?.zip ?? ''),
    },
  };
};

const getPrimarySubscriberValue = (isPrimary: boolean, value: string) => isPrimary ? '' : value;

export const InsuranceInfo: React.VFC<InsuranceInfoProps> = props => {
  const currentUser = useCurrentuser();
  const { data: userInsurances = [], isInitialLoading } = useUserInsurancesQuery(currentUser.id);

  if (isInitialLoading) {
    return null;
  }

  const initialValues = getInitialValues(props.insuranceId, userInsurances[0]?.insurance);

  return (
    <AddressVerificationProvider
      shouldReverifyAddress={false}
      parsedVerifiedAddress={initialValues.address.address1 ? initialValues.address : null}
    >
      <InsuranceInfoContent {...props} initialValues={initialValues} />
    </AddressVerificationProvider>
  );
};
